import { useTranslation } from "react-i18next";

type Props = {
    /** One value per bar, 0..1. Empty while the take is still being read. */
    peaks: number[];
    /** How far through the recording playback is, 0..1. */
    progress?: number;
    /** Given a 0..1 position when the lane is clicked. */
    onSeek?: (fraction: number) => void;
};

/**
 * The recording, drawn as bars.
 *
 * SVG rather than a canvas: the bars are a handful of rectangles, they scale
 * with the lane, and they take the theme's colours without a redraw. Played
 * bars are brand-coloured and the rest are muted, so the playhead is the
 * boundary between the two rather than a line to hunt for.
 */
export default function Waveform({ peaks, progress = 0, onSeek }: Props) {
    const { t } = useTranslation();
    const played = Math.round(peaks.length * Math.min(Math.max(progress, 0), 1));

    const seek = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!onSeek) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (bounds.width === 0) return;
        onSeek(Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 1));
    };

    if (peaks.length === 0) {
        return (
            <div
                data-testid="waveform-empty"
                className="flex h-20 items-center justify-center rounded-xl bg-surface-sunken text-xs text-content-tertiary"
            >
                {t("meditation.studio.noTake")}
            </div>
        );
    }

    return (
        <div
            role={onSeek ? "button" : undefined}
            tabIndex={onSeek ? 0 : undefined}
            aria-label={onSeek ? t("meditation.studio.seek") : undefined}
            onClick={seek}
            onKeyDown={(event) => {
                if (!onSeek) return;
                if (event.key === "ArrowLeft") onSeek(Math.max(progress - 0.05, 0));
                if (event.key === "ArrowRight") onSeek(Math.min(progress + 0.05, 1));
            }}
            data-testid="waveform"
            className={`h-20 rounded-xl bg-surface-sunken p-2 ${
                onSeek ? "cursor-pointer" : ""
            }`}
        >
            <svg
                viewBox={`0 0 ${peaks.length} 100`}
                preserveAspectRatio="none"
                className="h-full w-full"
                aria-hidden="true"
            >
                {peaks.map((peak, index) => {
                    // Every bar keeps a sliver of height, so a silent stretch
                    // reads as part of the recording rather than as its end.
                    const height = Math.max(peak * 92, 3);
                    return (
                        <rect
                            key={index}
                            x={index + 0.15}
                            y={(100 - height) / 2}
                            width={0.7}
                            height={height}
                            rx={0.35}
                            className={
                                index < played
                                    ? "fill-brand-500"
                                    : "fill-gray-300 dark:fill-gray-600"
                            }
                        />
                    );
                })}
            </svg>
        </div>
    );
}
